import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import axiosInstance from "../interceptors/axiosInterceptor";
import { SUPER_ADMIN_ROLE } from "../constants/permissions";

const AuthContext = createContext({
  user: null,
  role: null,
  privileges: [],
  isSuperAdmin: false,
  loading: true,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  refreshUser: () => {},
  logout: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_URL}/api/auth/fetchUser`,
      );
      if (response?.data?.success && response?.data?.data?.user) {
        setUser(response.data.data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Failed to fetch current user:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  // Listen for storage changes (e.g., login/logout in another tab)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "token") {
        fetchCurrentUser();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [fetchCurrentUser]);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("authUser");
    setUser(null);
  }, []);

  const role = user?.role?.name || null;
  const privileges = useMemo(() => user?.role?.privileges || [], [user]);
  const isSuperAdmin = role === SUPER_ADMIN_ROLE;

  /**
   * Check if the user has a specific permission.
   * Super admins always return true.
   */
  const hasPermission = useCallback(
    (permission) => {
      if (isSuperAdmin) return true;
      return privileges.includes(permission);
    },
    [isSuperAdmin, privileges],
  );

  /**
   * Check if the user has at least one of the given permissions.
   * Super admins always return true.
   */
  const hasAnyPermission = useCallback(
    (permissionList) => {
      if (isSuperAdmin) return true;
      if (!permissionList || permissionList.length === 0) return true;
      return permissionList.some((p) => privileges.includes(p));
    },
    [isSuperAdmin, privileges],
  );

  const value = {
    user,
    role,
    privileges,
    isSuperAdmin,
    loading,
    hasPermission,
    hasAnyPermission,
    refreshUser: fetchCurrentUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context (user, role, permissions).
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
